import React, { useState, useEffect } from 'react';

const COLOR_SWATCHES = {
  Red: '#EF4444', Blue: '#3B82F6', Black: '#1F2937', White: '#F9FAFB',
  Green: '#10B981', Yellow: '#F59E0B', Pink: '#EC4899', Purple: '#8B5CF6',
  Orange: '#F97316', Gray: '#6B7280', Brown: '#92400E', Navy: '#1E3A5F',
};

/**
 * VariantSelector
 * Props:
 *   options     - [{ name: 'Color', values: ['Red','Blue'] }]
 *   variants    - [{ _id, name, price, stock, image, attributes: { Color:'Red', Size:'S' } }]
 *   basePrice   - product base price
 *   onSelect    - callback(selectedVariant | null)
 */
const VariantSelector = ({ options = [], variants = [], basePrice = 0, onSelect }) => {
  const [selected, setSelected] = useState({}); // { Color: 'Red', Size: 'S' }

  // Auto-select first value for each option
  useEffect(() => {
    const initial = {};
    options.forEach(opt => {
      if (opt.values?.length > 0) initial[opt.name] = opt.values[0];
    });
    setSelected(initial);
  }, [options]);

  // Find matching variant
  useEffect(() => {
    if (Object.keys(selected).length === 0) { onSelect?.(null); return; }
    const match = variants.find(v => {
      const attrs = v.attributes instanceof Map ? Object.fromEntries(v.attributes) : (v.attributes || {});
      return Object.keys(selected).every(k => attrs[k] === selected[k]);
    });
    onSelect?.(match || null);
  }, [selected, variants]);

  const handleSelect = (optName, value) => {
    setSelected(prev => ({ ...prev, [optName]: value }));
  };

  // Check if a specific value leads to any available variant
  const isAvailable = (optName, value) => {
    const testSelection = { ...selected, [optName]: value };
    return variants.some(v => {
      const attrs = v.attributes instanceof Map ? Object.fromEntries(v.attributes) : (v.attributes || {});
      return Object.keys(testSelection).every(k => attrs[k] === testSelection[k]) && v.isActive !== false && (v.stock === undefined || v.stock > 0);
    });
  };

  if (!options?.length) return null;

  return (
    <div style={{ marginBottom: '16px' }}>
      {options.map(opt => (
        <div key={opt.name} style={{ marginBottom: '14px' }}>
          <label style={{ fontSize: '13px', fontWeight: 700, color: '#374151', marginBottom: '6px', display: 'block' }}>
            {opt.name}: <span style={{ fontWeight: 400, color: '#6B7280' }}>{selected[opt.name] || 'Select'}</span>
          </label>

          <div className="d-flex flex-wrap gap-2">
            {opt.values.map(val => {
              const isActive = selected[opt.name] === val;
              const available = isAvailable(opt.name, val);
              const isColor = opt.name === 'Color';
              const swatch = COLOR_SWATCHES[val];

              if (isColor && swatch) {
                // Color swatch button
                return (
                  <button key={val} type="button" onClick={() => available && handleSelect(opt.name, val)}
                    title={val}
                    style={{
                      width: '36px', height: '36px', borderRadius: '50%', cursor: available ? 'pointer' : 'not-allowed',
                      background: swatch, opacity: available ? 1 : 0.3,
                      border: isActive ? '3px solid #B91C1C' : '2px solid #D1D5DB',
                      outline: isActive ? '2px solid #FEE2E2' : 'none',
                      transition: 'all 0.15s',
                      position: 'relative',
                    }}>
                    {isActive && (
                      <span style={{
                        position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: ['White', 'Yellow'].includes(val) ? '#1F2937' : '#FFF', fontWeight: 800, fontSize: '14px',
                      }}>✓</span>
                    )}
                  </button>
                );
              }

              // Text button (size, material, etc.)
              return (
                <button key={val} type="button" onClick={() => available && handleSelect(opt.name, val)}
                  style={{
                    padding: '6px 16px', borderRadius: '8px', fontSize: '13px', fontWeight: 600,
                    cursor: available ? 'pointer' : 'not-allowed',
                    border: isActive ? '2px solid #B91C1C' : '1px solid #D1D5DB',
                    background: isActive ? '#FEF2F2' : available ? '#FFF' : '#F9FAFB',
                    color: isActive ? '#B91C1C' : available ? '#1F2937' : '#D1D5DB',
                    textDecoration: available ? 'none' : 'line-through',
                    transition: 'all 0.15s',
                  }}>
                  {val}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default VariantSelector;
